import { useCallback, useEffect, useState } from "react";
import { dashboardApi, type RationalDeviceSnapshot } from "../../api/dashboardApi";
import { useAuth } from "../../auth/AuthContext";
import "../../styles/rational.css";

type WidgetData = {
  name: string;
  type: string;
  value?: number | string;
  data?: Array<{ name: string; value: number }>;
  [k: string]: any;
};

export function RationalDashboardWidgets() {
  const { token } = useAuth() as any;
  const [loading, setLoading] = useState(true);
  const [dashboards, setDashboards] = useState<any[]>([]);
  const [config, setConfig] = useState<any[]>([]);
  const [devices, setDevices] = useState<RationalDeviceSnapshot[]>([]);
  const [devicesGroupName, setDevicesGroupName] = useState<string>("");
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [selectedDeviceDetail, setSelectedDeviceDetail] = useState<RationalDeviceSnapshot | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kitchenAlarms, setKitchenAlarms] = useState<{ errorActive?: string[]; ovenOffline?: string[] } | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const from = new Date();
    from.setDate(from.getDate() - 7);
    const to = new Date();
    Promise.allSettled([
      dashboardApi.getRationalDashboard(token),
      dashboardApi.getRationalDevices(token),
      dashboardApi.getKitchenAnalytics(token, { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) }),
    ])
      .then(([dashboardRes, devicesRes, kitchenRes]) => {
        if (dashboardRes.status === "fulfilled") {
          const res: any = dashboardRes.value;
          const payload = res?.data ?? res;
          setDashboards(payload.dashboards ?? []);
          setConfig(payload.config ?? []);
          if (payload?.error) setError(payload.error);
        } else {
          const e: any = dashboardRes.reason;
          setError(e?.message ?? "Errore caricamento dashboard Rational");
        }

        if (devicesRes.status === "fulfilled") {
          const res: any = devicesRes.value;
          const payload = res?.data ?? res;
          const items = Array.isArray(payload?.items) ? payload.items : [];
          setDevices(items);
          setDevicesGroupName(payload?.groupName ?? "");
          if (items.length > 0) {
            setSelectedDeviceId((prev) => (prev && items.some((d: RationalDeviceSnapshot) => d.id === prev) ? prev : items[0].id));
          } else {
            setSelectedDeviceId(null);
          }
        } else {
          setDevices([]);
          setDevicesGroupName("");
        }
        if (kitchenRes?.status === "fulfilled" && kitchenRes?.value) {
          const ka = kitchenRes.value as any;
          if (ka?.notifications) setKitchenAlarms(ka.notifications);
        } else {
          setKitchenAlarms(null);
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const from = new Date();
    from.setDate(from.getDate() - 7);
    const to = new Date();
    const refreshDevices = () => {
      Promise.all([
        dashboardApi.getRationalDevices(token),
        dashboardApi.getKitchenAnalytics(token, { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) }),
      ]).then(([devRes, kaRes]: any[]) => {
        const payload = devRes?.data ?? devRes;
        const items = Array.isArray(payload?.items) ? payload.items : [];
        setDevices(items);
        if (payload?.groupName) setDevicesGroupName(payload.groupName);
        const ka = kaRes?.data ?? kaRes;
        if (ka?.notifications) setKitchenAlarms(ka.notifications);
      }).catch(() => {});
    };
    const interval = setInterval(refreshDevices, 90 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchDeviceDetail = useCallback(() => {
    if (!token || selectedDeviceId == null) return;
    setDetailLoading(true);
    dashboardApi.getRationalDeviceDetail(token, selectedDeviceId)
      .then((res: any) => {
        const payload = res?.data ?? res;
        setSelectedDeviceDetail(payload ?? null);
        setDetailError(null);
      })
      .catch((e: any) => {
        setSelectedDeviceDetail(null);
        setDetailError(e?.message ?? "Errore caricamento dettaglio dispositivo");
      })
      .finally(() => setDetailLoading(false));
  }, [token, selectedDeviceId]);

  useEffect(() => {
    if (!token || selectedDeviceId == null) {
      setSelectedDeviceDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }
    fetchDeviceDetail();
  }, [token, selectedDeviceId, fetchDeviceDetail]);

  // Auto-refresh dettaglio live ogni 5 minuti
  useEffect(() => {
    if (!token || selectedDeviceId == null) return;
    const interval = setInterval(fetchDeviceDetail, 300_000);
    return () => clearInterval(interval);
  }, [token, selectedDeviceId, fetchDeviceDetail]);

  if (loading) {
    return (
      <div className="card shadow-sm p-4">
        <div className="text-muted d-flex align-items-center gap-2">
          <div className="spinner-border spinner-border-sm" />
          Caricamento dashboard Connected Cooking…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning">
        <strong>Dashboard Rational</strong>: {error}
        <div className="small mt-2">
          Verifica che l&apos;account Rational abbia <code>meta.groupId</code> configurato e che le credenziali siano valide.
        </div>
      </div>
    );
  }

  // Prefer widgets from API dashboards (with data), fallback to config (layout only)
  const allWidgets: WidgetData[] = [];
  const fromApi = dashboards[0]?.widgets ?? dashboards.find((d: any) => Array.isArray(d?.widgets))?.widgets;
  const source = Array.isArray(fromApi) && fromApi.length > 0 ? fromApi : config.flatMap((d: any) => d?.widgets ?? []);
  for (const w of source) {
    if (w && (w.name || w.type)) {
      allWidgets.push({
        name: w.name ?? "Widget",
        type: w.type ?? "number",
        ...w,
      });
    }
  }

  const selectedDevice = devices.find((d) => d.id === selectedDeviceId) ?? devices[0] ?? null;
  // Dettaglio sempre valido se presente: è stato richiesto per selectedDeviceId
  const detail = selectedDeviceDetail;
  const raw = selectedDevice?.raw ?? detail?.raw ?? selectedDevice ?? detail;
  const getVal = (keys: string[], fallback: string | number | null = "—") => {
    const sources = [detail, selectedDevice, raw];
    for (const k of keys) {
      for (const src of sources) {
        const v = src?.[k as keyof typeof src];
        if (v != null && v !== "") return v;
      }
      // Fallback: proprietà annidate in raw (es. raw.state, raw.telemetry)
      if (raw && typeof raw === "object") {
        const nested = (raw as any).state ?? (raw as any).telemetry ?? (raw as any).data;
        const v = nested?.[k];
        if (v != null && v !== "") return v;
      }
    }
    return fallback;
  };
  const selectedDeviceData = selectedDevice
    ? {
        ...selectedDevice,
        ...(detail ?? {}),
        raw,
        chamberTemperature: getVal(["chamberTemperature", "chamberTemp", "temperatureChamber"]),
        foodTemperature: getVal(["foodTemperature", "foodTemp", "temperatureFood", "coreTemperature"]),
        humidity: getVal(["humidity", "humidityPercent"]),
        doorStatus: getVal(["doorStatus", "doorState", "door"]),
        cleaningPercentage: getVal(["cleaningPercentage"], null) ?? selectedDevice.cleaningPercentage ?? raw?.cleaningPercentage ?? null,
        maintenancePercentage: getVal(["maintenancePercentage"], null) ?? selectedDevice.maintenancePercentage ?? raw?.maintenancePercentage ?? null,
        cookingProgram: getVal(["cookingProgram", "currentProgram", "programName"]),
        cookingProgramStep: getVal(["cookingProgramStep", "programPhase", "programStep"], "") || "",
        deviceIp: getVal(["deviceIp", "ipAddress", "ip"]),
        name: getVal(["name", "deviceName", "title"]),
        deviceModel: getVal(["deviceModel", "model"]),
        deviceStatus: getVal(["deviceStatus", "status", "connectionStatus"]),
        currentErrors: Array.isArray(detail?.currentErrors)
          ? detail.currentErrors
          : Array.isArray(selectedDevice?.currentErrors)
            ? selectedDevice.currentErrors
            : Array.isArray(raw?.currentErrors)
              ? raw.currentErrors
              : [],
      }
    : null;
  const connLabel = (ct: string | null | undefined) =>
    /wifi|wi-fi|wireless|wlan/i.test(String(ct ?? "")) ? "Wi‑Fi" : /lan|ethernet|via cavo/i.test(String(ct ?? "")) ? "LAN" : (ct ?? "—");

  const isDeviceInAlarm = (d: RationalDeviceSnapshot) => {
    const hasErrors = Array.isArray(d.currentErrors) && d.currentErrors.some((e: any) => e?.active !== false);
    const statusStr = String(d.deviceStatus ?? (d as any).raw?.deviceStatus ?? (d as any).raw?.status ?? "").toLowerCase();
    const statusAlarm = /alarm|error|fault|errore|device has error|has.?error|unhealthy/i.test(statusStr);
    const chamberId = Number(d.id ?? d.deviceId ?? 0);
    const inErrorActiveChambers = Array.isArray(kitchenAlarms?.errorActiveChamberIds) &&
      chamberId > 0 &&
      kitchenAlarms!.errorActiveChamberIds.includes(chamberId);
    const inKitchenErrorByName = Array.isArray(kitchenAlarms?.errorActive) && kitchenAlarms!.errorActive.some(
      (name) => name && (d.name?.toLowerCase().includes(name.toLowerCase()) || d.serialNumber?.includes(name))
    );
    return hasErrors || statusAlarm || inErrorActiveChambers || inKitchenErrorByName;
  };

  const onlineCount = devices.filter((d) => String(d.deviceStatus ?? "").toLowerCase().includes("online")).length;
  const alarmsCount = devices.filter(isDeviceInAlarm).length;
  const userActionCount = devices.filter((d) => Boolean(d.userActionRequired)).length;
  const wifiCount = devices.filter(
    (d) => /wifi|wi-fi|wireless/i.test(String(d.connectionType ?? ""))
  ).length;

  if (allWidgets.length === 0 && dashboards.length === 0 && devices.length === 0) {
    return (
      <div className="alert alert-info">
        Nessun dato disponibile dalla dashboard Connected Cooking.
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <div className="rational-hero">
        <div className="d-flex align-items-center gap-3">
          <div className="rational-widget-value" style={{ fontSize: '3rem', color: 'white' }}>
            <i className="bi bi-cpu" />
          </div>
          <div>
            <h2 className="mb-1">Rational Dashboard</h2>
            <p className="mb-0 opacity-90">Connected Cooking Platform</p>
          </div>
        </div>
      </div>

      <div className="rational-card mb-4">
        <div className="rational-card-header">
          <i className="bi bi-speedometer2" />
          Dashboard Rational (Connected Cooking)
        </div>
        <div className="card-body">
          <div className="row g-4">
            {allWidgets.map((w, idx) => (
              <div
                key={`${w.name}-${idx}`}
                className={`col-12 ${
                  w.type === "pieChart" || w.type === "topEntries" ? "col-md-6 col-lg-4" : "col-md-6 col-lg-3"
                }`}
              >
                <div className="rational-widget">
                  <div className="rational-widget-title">{w.name}</div>
                  {w.type === "number" && (
                    <div className="rational-widget-value">
                      {w.value != null ? w.value : w.data ?? "—"}
                    </div>
                  )}
                  {w.type === "pieChart" && (
                    <div>
                      {Array.isArray(w.data) && w.data.length > 0 ? (
                        <ul className="rational-widget-list">
                          {w.data.slice(0, 5).map((d: any, i: number) => (
                            <li key={i}>
                              {d.name ?? d.label}: <strong>{d.value ?? d.count}</strong>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-muted">Dati non disponibili</span>
                      )}
                    </div>
                  )}
                  {w.type === "topEntries" && (
                    <div>
                      {Array.isArray(w.data) && w.data.length > 0 ? (
                        <ol className="rational-widget-list" style={{ listStyle: 'decimal', paddingLeft: '1.5rem' }}>
                          {w.data.slice(0, 5).map((d: any, i: number) => (
                            <li key={i}>
                              {d.name ?? d.label}: <strong>{d.value ?? d.count}</strong>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <span className="text-muted">Dati non disponibili</span>
                      )}
                    </div>
                  )}
                  {!["number", "pieChart", "topEntries"].includes(w.type) && (
                    <div className="small text-muted">
                      Tipo: {w.type} — {JSON.stringify(w.data ?? w.value ?? "—")}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {dashboards.length > 0 && allWidgets.length === 0 && (
            <div className="text-muted small mt-3">
              Dashboard ricevuta ({dashboards.length} elementi). La struttura dati potrebbe differire.
            </div>
          )}
        </div>
      </div>

      {/* Programmi in esecuzione (da cloud chambers.view – gruppo Il mio ristorante) */}
      <div className="rational-card mb-4">
        <div className="rational-card-header">
          <i className="bi bi-play-circle" />
          Programmi in esecuzione
        </div>
        <div className="card-body">
          <div className="small text-muted mb-3">
            Dati da Connected Cooking Cloud · Gruppo <strong>{devicesGroupName || "Il mio ristorante"}</strong>
          </div>
          {(() => {
            const progStr = (v: unknown): string => {
              if (v == null) return "";
              if (typeof v === "string") return v.trim();
              if (typeof v === "object" && v !== null && "text" in v) return String((v as { text?: string }).text ?? "").trim();
              return String(v).trim();
            };
            const withProgram = devices.filter(
              (d) => progStr(d.cookingProgram ?? (d as any).programName ?? d.raw?.cookingProgram).length > 0
            );
            if (withProgram.length === 0) {
              return (
                <div className="text-muted small py-3">
                  Nessun programma in esecuzione al momento.
                </div>
              );
            }
            return (
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Dispositivo</th>
                      <th>Programma</th>
                      <th>Step</th>
                      <th>Temp.</th>
                      <th>Rimasto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withProgram.map((d) => {
                      const prog = progStr(d.cookingProgram ?? (d as any).programName ?? d.raw?.cookingProgram) || "—";
                      const step = d.cookingProgramStep ?? (d as any).programPhase ?? d.raw?.cookingProgramStep ?? "—";
                      const timeLeft = d.timeLeft != null
                        ? (typeof d.timeLeft === "number" ? `${d.timeLeft} min` : String(d.timeLeft))
                        : "—";
                      return (
                        <tr
                          key={d.id}
                          onClick={() => setSelectedDeviceId(d.id)}
                          style={{ cursor: "pointer" }}
                        >
                          <td>
                            <span className="fw-semibold">{d.name ?? d.serialNumber ?? "—"}</span>
                            {d.serialNumber && d.name && (
                              <div className="small text-muted">{d.serialNumber}</div>
                            )}
                          </td>
                          <td>{prog}</td>
                          <td>{step}</td>
                          <td>
                            {d.chamberTemperature ?? d.foodTemperature ?? "—"}
                          </td>
                          <td>{timeLeft}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="rational-card mb-4">
        <div className="rational-card-header">
          <i className="bi bi-hdd-network" />
          Dispositivi Connected Cooking
        </div>
        <div className="card-body">
          <div className="small text-muted mb-3">
            Gruppo: <strong>{devicesGroupName || "Il mio ristorante"}</strong> · Lista da <code>chambers.view</code>
          </div>

          {/* Sezione Allarmi: notifiche nome macchina + tipo allarme */}
          {alarmsCount > 0 && (
            <div className="alert alert-danger border-danger mb-4 d-flex align-items-start gap-3">
              <div className="flex-shrink-0 fs-4">
                <i className="bi bi-exclamation-triangle-fill" />
              </div>
              <div className="flex-grow-1">
                <h6 className="alert-heading mb-2">Allarmi attivi</h6>
                <p className="mb-2 small">Dispositivi con allarme: clicca per aprire il dettaglio.</p>
                <ul className="list-unstyled mb-0">
                  {devices.filter(isDeviceInAlarm).map((d) => {
                    const errMsg =
                      Array.isArray(d.currentErrors) && d.currentErrors.length > 0
                        ? (d.currentErrors as any[]).map((e: any) => e?.errorCodeTranslation ?? e?.errorCode ?? e?.message ?? "Allarme").join(" · ")
                        : String(d.deviceStatus ?? "Allarme");
                    return (
                      <li key={d.id} className="d-flex align-items-center gap-2 py-1">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-danger text-start text-decoration-none"
                          onClick={() => setSelectedDeviceId(d.id)}
                        >
                          <strong>{d.name ?? d.serialNumber ?? `Dispositivo ${d.id}`}</strong>
                          <span className="text-muted ms-1">— {errMsg}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}

          {/* Sezione 1: Panoramica dispositivi - Card come da chambers.view */}
          <div className="mb-4">
            <h6 className="text-muted mb-3">
              <i className="bi bi-grid-3x3-gap me-2" />
              Panoramica dispositivi
            </h6>
            <div className="row g-3">
              {devices.map((d) => {
                const isOnline = String(d.deviceStatus ?? "").toLowerCase().includes("online");
                const hasAlarms = isDeviceInAlarm(d);
                const isSelected = selectedDeviceId === d.id;
                return (
                  <div
                    key={d.id}
                    className="col-12 col-md-6 col-lg-4"
                    onClick={() => setSelectedDeviceId(d.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className={`border rounded p-3 h-100 transition-shadow ${
                        isSelected ? "border-primary shadow" : ""
                      }`}
                    >
                      <div className="d-flex align-items-start gap-3">
                        <div className="flex-shrink-0">
                          {(d.chamberDefaultThumbnailUrl || d.chamberDefaultImageUrl) ? (
                            <img
                              src={d.chamberDefaultThumbnailUrl ?? d.chamberDefaultImageUrl ?? ""}
                              alt={d.name ?? ""}
                              className="rounded"
                              style={{ width: 64, height: 64, objectFit: "contain" }}
                            />
                          ) : (
                            <div
                              className="bg-light rounded d-flex align-items-center justify-content-center"
                              style={{ width: 64, height: 64 }}
                            >
                              <i className="bi bi-hdd-network-fill text-muted" />
                            </div>
                          )}
                        </div>
                        <div className="flex-grow-1 min-w-0">
                          <div className="d-flex justify-content-between align-items-start gap-2">
                            <h6 className="mb-1 text-truncate">{d.name ?? "—"}</h6>
                            <span
                              className={`badge flex-shrink-0 ${
                                hasAlarms
                                  ? "text-bg-danger"
                                  : isOnline
                                  ? "text-bg-success"
                                  : "text-bg-secondary"
                              }`}
                            >
                              {hasAlarms ? "Allarme" : (d.deviceStatus ?? "—")}
                            </span>
                          </div>
                          <div className="small text-muted text-truncate">{d.serialNumber ?? "—"}</div>
                          <div className="mt-2 small">
                            <div className="d-flex flex-wrap gap-2 align-items-center">
                              <span
                                className={`badge ${
                                  String(d.doorStatus ?? "").toLowerCase().includes("aperto")
                                    ? "text-bg-warning"
                                    : "text-bg-light text-dark"
                                }`}
                              >
                                <i className="bi bi-door-open me-1" />
                                {d.doorStatus ?? "—"}
                              </span>
                              <span title="Programma"><strong>Programma:</strong> {d.cookingProgram ?? "—"}</span>
                            </div>
                            <div className="mt-2 d-flex flex-wrap gap-3">
                              <span title="Temp. camera"><i className="bi bi-thermometer-half me-1 text-muted" />Temp. camera: <strong>{d.chamberTemperature ?? "—"}</strong></span>
                              <span title="Temp. sonda"><i className="bi bi-thermometer me-1 text-muted" />Temp. sonda: <strong>{d.foodTemperature ?? "—"}</strong></span>
                            </div>
                            <div className="mt-2">
                              <span className="badge bg-light text-dark border">
                                <i className={`bi ${/wifi|wi-fi|wireless|wlan/i.test(String(d.connectionType ?? "")) ? "bi-wifi" : "bi-ethernet"} me-1`} />
                                Connessione: {connLabel(d.connectionType)}
                              </span>
                            </div>
                          </div>
                          {hasAlarms && (
                            <span className="badge text-bg-danger mt-2">Errori attivi</span>
                          )}
                          {d.userActionRequired && (
                            <span className="badge text-bg-warning ms-1 mt-2">Azione richiesta</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {devices.length === 0 && (
              <div className="text-center text-muted py-4 small">
                Nessun dispositivo Rational disponibile.
              </div>
            )}
          </div>

          {/* Sezione 2: KPIs riepilogo */}
          <div className="row g-3 mb-4">
            <div className="col-6 col-lg-3">
              <div className="border rounded p-3 h-100">
                <div className="small text-muted">Dispositivi</div>
                <div className="fs-4 fw-bold">{devices.length}</div>
              </div>
            </div>
            <div className="col-6 col-lg-3">
              <div className="border rounded p-3 h-100">
                <div className="small text-muted">Online</div>
                <div className="fs-4 fw-bold text-success">{onlineCount}</div>
              </div>
            </div>
            <div className="col-6 col-lg-3">
              <div className={`border rounded p-3 h-100 ${alarmsCount > 0 ? "border-danger bg-danger bg-opacity-10" : ""}`}>
                <div className="small text-muted">Con allarmi</div>
                <div className={`fs-4 fw-bold ${alarmsCount > 0 ? "text-danger" : ""}`}>{alarmsCount}</div>
              </div>
            </div>
            <div className="col-6 col-lg-3">
              <div className="border rounded p-3 h-100">
                <div className="small text-muted">Azione richiesta</div>
                <div className="fs-4 fw-bold text-warning">{userActionCount}</div>
              </div>
            </div>
            <div className="col-6 col-lg-3">
              <div className="border rounded p-3 h-100">
                <div className="small text-muted mb-2">Connessione Wi-Fi</div>
                <div className="d-flex gap-2 align-items-center">
                  <span
                    className={`badge px-3 py-2 ${wifiCount > 0 ? "bg-success" : "bg-secondary"}`}
                    style={{ fontSize: "0.9rem" }}
                    title={wifiCount > 0 ? "Accesso ON" : "Accesso OFF"}
                  >
                    <i className="bi bi-power me-1" />
                    ON
                  </span>
                  <span
                    className={`badge px-3 py-2 ${wifiCount === 0 ? "bg-danger" : "bg-secondary"}`}
                    style={{ fontSize: "0.9rem" }}
                    title={wifiCount === 0 ? "Accesso OFF" : "Accesso ON"}
                  >
                    <i className="bi bi-power me-1" />
                    OFF
                  </span>
                </div>
                <div className="small text-muted mt-1">{wifiCount} su {devices.length} via Wi‑Fi</div>
              </div>
            </div>
          </div>

          {/* Sezione 3: Tabella dispositivi */}
          <h6 className="text-muted mb-3">
            <i className="bi bi-table me-2" />
            Tabella dispositivi
          </h6>
          <div className="table-responsive mb-4" style={{ maxHeight: 360, overflowY: "auto" }}>
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  <th>Nome</th>
                  <th>Seriale</th>
                  <th>Stato</th>
                  <th>Programma</th>
                  <th>Step</th>
                  <th>Porta</th>
                  <th>Temp. camera</th>
                  <th>Temp. sonda</th>
                  <th>Umidità</th>
                  <th>Pulizia %</th>
                  <th>Manut. %</th>
                  <th>Rimasto</th>
                  <th>Conn.</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {devices.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="text-center text-muted py-3">
                      Nessun dispositivo Rational disponibile.
                    </td>
                  </tr>
                ) : (
                  devices.map((d) => {
                    const timeLeft = d.timeLeft != null ? (typeof d.timeLeft === "number" ? `${d.timeLeft} min` : String(d.timeLeft)) : "—";
                    const inAlarm = isDeviceInAlarm(d);
                    return (
                      <tr
                        key={d.id}
                        onClick={() => setSelectedDeviceId(d.id)}
                        style={{ cursor: "pointer" }}
                        className={selectedDeviceId === d.id ? "table-primary" : inAlarm ? "table-danger" : ""}
                      >
                        <td className="fw-semibold">{d.name ?? "—"}</td>
                        <td className="text-nowrap">{d.serialNumber ?? "—"}</td>
                        <td>
                          {inAlarm ? <span className="badge text-bg-danger">Allarme</span> : (d.deviceStatus ?? "—")}
                        </td>
                        <td>{d.cookingProgram ?? "—"}</td>
                        <td>{d.cookingProgramStep ?? "—"}</td>
                        <td>{d.doorStatus ?? "—"}</td>
                        <td>{d.chamberTemperature ?? "—"}</td>
                        <td>{d.foodTemperature ?? "—"}</td>
                        <td>{d.humidity ?? "—"}</td>
                        <td>{d.cleaningPercentage != null ? `${d.cleaningPercentage}%` : "—"}</td>
                        <td>{d.maintenancePercentage != null ? `${d.maintenancePercentage}%` : "—"}</td>
                        <td>{timeLeft}</td>
                        <td>{connLabel(d.connectionType)}</td>
                        <td className="text-nowrap">{d.deviceIp ?? "—"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Sezione 4: Dettaglio live dispositivo selezionato (auto-refresh 5min) */}
          {selectedDeviceData && (
            <div className="border rounded p-3">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="text-muted mb-0">
                  <i className="bi bi-broadcast me-2" />
                  Dettaglio live
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={fetchDeviceDetail}
                    disabled={detailLoading}
                    title="Aggiorna dati live"
                  >
                    <i className={`bi ${detailLoading ? "bi-arrow-repeat" : "bi-arrow-clockwise"}`} />
                  </button>
                  {detailLoading && <span className="spinner-border spinner-border-sm text-muted" title="Aggiornamento…" />}
                  <span className="badge bg-light text-dark border small">auto 5min</span>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-12 col-lg-4">
                  <img
                    src={selectedDeviceData.chamberDefaultImageUrl ?? selectedDeviceData.chamberDefaultThumbnailUrl ?? "/images/rational-icombi-pro.png"}
                    alt={selectedDeviceData.name ?? "Rational iCombi Pro"}
                    className="img-fluid rounded border"
                  />
                </div>
                <div className="col-12 col-lg-8">
                  {detailError && (
                    <div className="alert alert-warning py-2 px-3 small mb-3">
                      {detailError}
                    </div>
                  )}

                  {/* Nome, IP, Modello */}
                  <div className="d-flex justify-content-between align-items-start gap-3 mb-3 flex-wrap">
                    <div>
                      <h5 className="mb-1">{selectedDeviceData.name ?? "—"}</h5>
                      <div className="small text-muted">
                        <i className="bi bi-hdd-network me-1" />
                        {selectedDeviceData.deviceModel ?? "—"}
                        {(selectedDeviceData.deviceFamily ?? selectedDeviceData.serialNumber) && (
                          <> · {selectedDeviceData.deviceFamily ?? ""} {selectedDeviceData.serialNumber ?? ""}</>
                        )}
                      </div>
                      <div className="small text-muted mt-1">
                        <i className="bi bi-ethernet me-1" />
                        IP: {selectedDeviceData.deviceIp ?? "—"}
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <span className={`badge ${String(selectedDeviceData.deviceStatus ?? "").toLowerCase().includes("online") ? "text-bg-success" : "text-bg-secondary"}`}>
                        {selectedDeviceData.deviceStatus ?? "—"}
                      </span>
                      {selectedDeviceData.userActionRequired && <span className="badge text-bg-warning">Azione richiesta</span>}
                      {Array.isArray(selectedDeviceData.currentErrors) && selectedDeviceData.currentErrors.length > 0 && (
                        <span className="badge text-bg-danger">Allarmi attivi</span>
                      )}
                    </div>
                  </div>

                  {/* KPI principali: Porta, Temperatura, Umidità, Pulizia, Programma */}
                  <div className="row g-2 mb-3">
                    <div className="col-6 col-md-4">
                      <div className="rational-live-kpi border rounded p-2">
                        <div className="small text-muted"><i className="bi bi-door-open me-1" />Porta</div>
                        <div className={`fw-semibold ${String(selectedDeviceData.doorStatus ?? "").toLowerCase().includes("aperto") ? "text-warning" : ""}`}>
                          {selectedDeviceData.doorStatus ?? "—"}
                        </div>
                      </div>
                    </div>
                    <div className="col-6 col-md-4">
                      <div className="rational-live-kpi border rounded p-2">
                        <div className="small text-muted"><i className="bi bi-thermometer-half me-1" />Temp. camera</div>
                        <div className="fw-semibold">{selectedDeviceData.chamberTemperature ?? "—"}</div>
                      </div>
                    </div>
                    <div className="col-6 col-md-4">
                      <div className="rational-live-kpi border rounded p-2">
                        <div className="small text-muted"><i className="bi bi-thermometer-half me-1" />Temp. sonda</div>
                        <div className="fw-semibold">{selectedDeviceData.foodTemperature ?? "—"}</div>
                      </div>
                    </div>
                    <div className="col-6 col-md-4">
                      <div className="rational-live-kpi border rounded p-2">
                        <div className="small text-muted"><i className="bi bi-droplet-half me-1" />Umidità</div>
                        <div className="fw-semibold">{selectedDeviceData.humidity ?? "—"}</div>
                      </div>
                    </div>
                    <div className="col-6 col-md-4">
                      <div className="rational-live-kpi border rounded p-2">
                        <div className="small text-muted"><i className="bi bi-droplet me-1" />Stato pulizia</div>
                        <div className="fw-semibold">{selectedDeviceData.cleaningPercentage ?? "—"}%</div>
                      </div>
                    </div>
                    <div className="col-6 col-md-4">
                      <div className="rational-live-kpi border rounded p-2">
                        <div className="small text-muted"><i className="bi bi-gear me-1" />Programma attivo</div>
                        <div className="fw-semibold small">{selectedDeviceData.cookingProgram ?? "—"} {selectedDeviceData.cookingProgramStep ? `· ${selectedDeviceData.cookingProgramStep}` : ""}</div>
                      </div>
                    </div>
                  </div>

                  {/* Altri dati */}
                  <div className="row g-3 small">
                    <div className="col-6 col-md-4"><div className="text-muted">Manutenzione</div><div>{selectedDeviceData.maintenancePercentage ?? "—"}%</div></div>
                    <div className="col-6 col-md-4"><div className="text-muted">Connessione</div><div>{selectedDeviceData.connectionType ?? "—"}</div></div>
                    <div className="col-6 col-md-4"><div className="text-muted">MAC</div><div>{selectedDeviceData.deviceMac ?? "—"}</div></div>
                    <div className="col-6 col-md-4"><div className="text-muted">Seriale</div><div>{selectedDeviceData.serialNumber ?? "—"}</div></div>
                    <div className="col-6 col-md-4"><div className="text-muted">App version</div><div>{selectedDeviceData.applicationVersion ?? "—"}</div></div>
                  </div>

                  {Array.isArray(selectedDeviceData.deviceActions) && selectedDeviceData.deviceActions.length > 0 && (
                    <div className="mt-3">
                      <div className="small text-muted mb-2">Azioni disponibili</div>
                      <div className="d-flex flex-wrap gap-2">
                        {selectedDeviceData.deviceActions.map((action) => (
                          <span key={action} className="badge text-bg-light border">{action}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {Array.isArray(selectedDeviceData.currentErrors) && selectedDeviceData.currentErrors.length > 0 && (
                    <div className="mt-3">
                      <div className="small text-muted mb-2">Errori correnti</div>
                      <div className="table-responsive">
                        <table className="table table-sm mb-0">
                          <thead>
                            <tr>
                              <th>Descrizione</th>
                              <th>Codice</th>
                              <th>Attivo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedDeviceData.currentErrors.map((err: any, idx: number) => (
                              <tr key={`${err?.errorCode ?? idx}-${idx}`}>
                                <td className="fw-semibold">{err?.errorCodeTranslation ?? err?.errorCode ?? "—"}</td>
                                <td className="text-muted small">{err?.errorCode ?? "—"}</td>
                                <td>{String(err?.active ?? true)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <details className="mt-3">
                    <summary className="small text-primary" style={{ cursor: "pointer" }}>
                      Payload completo dispositivo
                    </summary>
                    <pre className="small bg-light border rounded p-3 mt-2 mb-0" style={{ maxHeight: 260, overflow: "auto" }}>
                      {JSON.stringify(selectedDeviceData.raw ?? selectedDeviceData, null, 2)}
                    </pre>
                  </details>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

